<!--
  @component ObservabilityIO — P1 control-plane lens.
  chain:   ctx.deployment.stream → this → ctx.observability
  reads:   the parent node's unified-event stream
  effects: NONE — projections only; never forks the taxonomy
           (the daemon's classifier is the sole authority).
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { OBSERVABILITY_IO_KEY } from "./deployment-io.js";
  import type { ObservabilityIOContext } from "./deployment-io.js";

  export { OBSERVABILITY_IO_KEY };
  export type { ObservabilityIOContext };

  export function useObservabilityIO(): ObservabilityIOContext {
    const ctx = getContext<ObservabilityIOContext | undefined>(
      OBSERVABILITY_IO_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useObservabilityIO() called outside of an ObservabilityIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  const ctx: ObservabilityIOContext = {
    get stream() {
      return parent.stream;
    },
    byOrigin(origin) {
      return parent.stream.filter((e) => e.origin === origin);
    },
    byOp(op) {
      return parent.stream.filter((e) => e.op === op);
    },
    digest() {
      const counts = new Map<string, number>();
      for (const e of parent.stream) {
        const k = `${e.origin}/${e.code}`;
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      return [...counts.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, n]) => `${k}=${n}`);
    },
  } satisfies ObservabilityIOContext;

  setContext(OBSERVABILITY_IO_KEY, ctx);
</script>

{@render children?.()}
